<template>
  <section id="welcome-section">
    <section class="container">
      <h1 class="heading-mobile-s--light heading-xl--light">
        <span class="heading-mobile-s--medium heading-l--medium">Verified</span> once.
        <span class="heading-mobile-s--medium heading-l--medium">Trusted</span> Everywhere.<br>
        Building trust that moves with people.
      </h1>
      <a href="#" v-scroll-to="'#recruit-section'">
        <Button size="lg" mobile="md" icon="arrow-down" text="Get to know us better" class="mt-55 mt-mobile-40" icon-class="down"/>
      </a>
    </section>
  </section>

  <section id="recruit-section">
    <section class="container">
      <p class="overline-s--medium text-uppercase mb-32 mb-mobile-16 letter-spacing-08em">Who we work for</p>
      <Tabs :tabs="tabs" :selected-tab="activeTab" :key="updateKeyIndex" :margin="5">
        <template v-slot:tab-employer>
          <section>
            <div class="d-flex justify-content-between flex-mobile-column">
              <h2 class="flex-1 heading-mobile-xs--regular heading-l--regular mr-32 width-50 line-height-64 line-height-mobile-36">
                Verify once. Onboard with confidence.
              </h2>
              <div class="flex-1 width-50 mt-mobile-16">
                <h4 class="body-mobile-m--regular body-l--regular mb-mobile-0 mb-32 mr-mobile-16 line-height-32 line-height-mobile-24">
                  Weevs helps employers replace repeated background checks with reusable, audit-ready attestations that are built for regulated hiring.
                </h4>
                <h4 class="body-mobile-m--regular body-l--regular mb-mobile-0 mb-32 mr-mobile-16 line-height-32 line-height-mobile-24">
                  Employers get clearer evidence, faster onboarding, and lower data exposure without weakening verification rigor.
                </h4>
                <div class="d-flex d-mobile-none">
                  <router-link :to="{ name: 'WeAreDifferent' }">
                    <Button text="Read more details" icon="arrow-right" type="secondary" size="lg"/>
                  </router-link>
                  <router-link :to="{ name: 'Contact' }">
                    <Button text="Get in touch" icon="arrow-right" class="ml-16" size="lg"/>
                  </router-link>
                </div>
              </div>
            </div>
            <div class="cards mt-mobile-32 pb-mobile-20">
              <ServiceCard v-for="(card, key) in tabsContents.employer.cards" :icon="card.icon" :title="card.title" :description="card.description" :key="key" class="flex-1 d-flex flex-column"/>
            </div>
            <div class="d-flex d-desktop-none mt-mobile-20">
              <router-link :to="{ name: 'WeAreDifferent' }">
                <Button text="Read more details" icon="arrow-right" size="lg" mobile="md"/>
              </router-link>
            </div>
          </section>
        </template>

        <template v-slot:tab-candidate>
          <section>
            <div class="d-flex justify-content-between flex-mobile-column">
              <h2 class="flex-1 heading-mobile-xs--regular heading-l--regular mr-32 width-50 line-height-64 line-height-mobile-36">
                Verify once. Carry it everywhere.
              </h2>
              <div class="flex-1 width-50 mt-mobile-16">
                <h4 class="body-mobile-m--regular body-l--regular mb-mobile-0 mb-32 mr-mobile-16 line-height-32 line-height-mobile-24">
                  Weevs gives candidates a way to carry verified professional credentials across employers without resubmitting the same documents repeatedly.
                </h4>
                <div class="d-flex d-mobile-none">
                  <router-link :to="{ name: 'WeAreDifferent' }">
                    <Button text="Read more details" icon="arrow-right" type="secondary" size="lg"/>
                  </router-link>
                  <router-link :to="{ name: 'Contact' }">
                    <Button text="Get in touch" icon="arrow-right" class="ml-16" size="lg"/>
                  </router-link>
                </div>
              </div>
            </div>
            <div class="d-flex d-desktop-none mt-mobile-20">
              <router-link :to="{ name: 'WeAreDifferent' }">
                <Button text="Read more details" icon="arrow-right" size="lg" mobile="md"/>
              </router-link>
            </div>
          </section>
        </template>

        <template v-slot:tab-recruiter>
          <section>
            <div class="d-flex justify-content-between flex-mobile-column">
              <h2 class="flex-1 heading-mobile-xs--regular heading-l--regular mr-32 width-50 line-height-64 line-height-mobile-36">
                Place faster. Verify smarter.
              </h2>
              <div class="flex-1 width-50 mt-mobile-16">
                <h4 class="body-mobile-m--regular body-l--regular mb-mobile-0 mb-32 mr-mobile-16 line-height-32 line-height-mobile-24">
                  Weevs helps recruitment teams reduce time-to-placement by reusing verified candidate credentials across multiple roles and clients.
                </h4>
                <div class="d-flex d-mobile-none">
                  <router-link :to="{ name: 'WeAreDifferent' }">
                    <Button text="Read more details" icon="arrow-right" type="secondary" size="lg"/>
                  </router-link>
                  <router-link :to="{ name: 'Contact' }">
                    <Button text="Get in touch" icon="arrow-right" class="ml-16" size="lg"/>
                  </router-link>
                </div>
              </div>
            </div>
            <div class="d-flex d-desktop-none mt-mobile-20">
              <router-link :to="{ name: 'WeAreDifferent' }">
                <Button text="Read more details" icon="arrow-right" size="lg" mobile="md"/>
              </router-link>
            </div>
          </section>
        </template>
      </Tabs>
    </section>
  </section>

  <section id="featured-client">
    <section class="container">
      <section class="top--featured">
        <p class="overline-s--medium text-uppercase mb-32 mb-mobile-16 letter-spacing-08em">Featured Industry</p>
        <div class="d-flex justify-content-between flex-mobile-column">
          <h2 class="flex-1 heading-mobile-s--regular heading-l--regular mr-32 width-50 line-height-64 line-height-mobile-42">
            <span class="heading-mobile-s--medium heading-l--medium">Financial Services:</span> regulated onboarding
          </h2>
          <div class="flex-1 width-50 mt-mobile-16">
            <h4 class="body-mobile-m--regular body-l--regular mb-32 line-height-32 line-height-mobile-24">
              Financial institutions operate in environments where hiring, compliance, auditability, and candidate experience all matter at once. Weevs helps banks and regulated firms verify professional claims with stronger evidence, reusable attestations, and less long-term exposure to sensitive documents.
            </h4>
            <div class="d-flex">
              <a href="/use-cases/financial-services">
                <Button text="Read case study" icon="arrow-right" type="secondary" size="lg" mobile="md" class="b-none"/>
              </a>
            </div>
          </div>
        </div>
      </section>
    </section>
  </section>

  <section id="articles">
    <section class="container">
      <p class="overline-s--medium text-uppercase mb-32 text-stone-gray mb-mobile-16 letter-spacing-08em">insights</p>
      <div class="d-flex justify-content-between flex-mobile-column">
        <h2 class="flex-1 heading-mobile-s--regular heading-l--regular mr-32 width-50 line-height-64 line-height-mobile-42">
          Industry insights
        </h2>
        <div class="flex-1 width-50 mt-mobile-16">
          <h4 class="body-mobile-m--regular mb-mobile-0 body-l--regular mb-32 line-height-32 line-height-mobile-24">We share practical thinking on professional trust, verification, AI risk, and the infrastructure needed for regulated hiring.</h4>
          <div class="d-flex d-mobile-none">
            <router-link :to="{ name: 'Insights' }">
              <Button text="Read all insights" icon="arrow-right" size="lg" mobile="md"/>
            </router-link>
          </div>
        </div>
      </div>

      <section class="article-cards">
        <ArticleCard v-for="article in articles" :item="article" :key="article.title"/>
      </section>

      <div class="d-flex d-desktop-none mt-20">
        <router-link :to="{ name: 'Insights' }">
          <Button text="Read all insights" icon="arrow-right" size="lg" mobile="md"/>
        </router-link>
      </div>
    </section>
  </section>
</template>

<script>
import Button from "@/components/Button";
import Tabs from "@/components/Tabs";
import ServiceCard from "@/components/cards/ServiceCard";
import ArticleCard from "@/components/cards/ArticleCard";

export default {
  name: 'Home',
  components: { ServiceCard, ArticleCard, Tabs, Button },
  data() {
    return {
      tabs: [
        { id: 'employer', title: 'Employer' },
        { id: 'candidate', title: 'Candidate' },
        { id: 'recruiter', title: 'Recruiter' },
      ],
      tabsContents: {
        employer: {
          cards: [
            { icon: 'tech-strategic', title: 'Reusable Attestations', description: 'Verify once and reuse trusted professional proofs across future onboarding journeys.' },
            { icon: 'agile', title: 'Audit-Ready Evidence', description: 'Every attestation records what was verified, how, when, by whom, and under which policy.' },
            { icon: 'product-manage', title: 'Faster Regulated Onboarding', description: 'Reduce delays by reusing valid claims and escalating only the exceptions that need human judgement.' },
            { icon: 'security', title: 'Privacy-Minimising by Design', description: 'Keep documents temporary by default while retaining durable proof of process and outcome.' },
          ]
        },
        candidate: { cards: [] },
        recruiter: { cards: [] }
      },
      articles: [
        { img: '/img/usecases/usecase-financial.png', title: 'Financial Services: reusable verification for regulated hiring', badge: 'USE CASES', created_at: '', author: { image: '', name: '' }, href: '/use-cases/financial-services' },
        { img: '/img/usecases/usecase-technology.png', title: 'Technology: verify fast-moving talent without slowing growth', badge: 'USE CASES', created_at: '', author: { image: '', name: '' }, href: '/use-cases/technology' },
        { img: '/img/usecases/usecase-recruitment.png', title: 'Recruitment: building trust infrastructure for modern hiring', badge: 'USE CASES', created_at: '', author: { image: '', name: '' }, href: '/use-cases/recruitment' },
      ],
      activeTab: 'employer',
      updateKeyIndex: 0
    }
  }
}
</script>
